import { useState } from 'react';

import { fetchPostSharedUserAction } from '@iwoca/lapi-client/iwocapay';

import { PayLaterDecisionModal } from './components/PayLaterDecisionModal/PayLaterDecisionModal';
import { HelpTextAccordion } from './HelpTextAccordion/HelpTextAccordion';
import { useGetValidIwocapayFormalOffers } from './hooks/useGetValidIwocapayFormalOffers';
import { usePayLaterDecisionFlow } from './hooks/usePayLaterDecisionFlow';
import {
  submitBuyerStepZeroNewCustomer,
  submitBuyerStepZeroReturningCustomer,
} from './PayLater/CreateUserForm/submitBuyerStepZero';
import { PaymentDetails } from './PaymentDetails/PaymentDetails';
import { FormValues, PayLink } from './utils/PayLinkLanding.types';
import {
  getBuyerInterest,
  getPricingPromotions,
} from './utils/productPricingHelpers';
import { redirectAfterPayLater } from './utils/redirectAfterPayLater';
import {
  SELLER_GET_ERROR,
  useGetPayLinkByPayLinkId,
  useGetSellerByHandle,
} from '../../api/lending/lapiHooks';
import { LoadingScreen } from '../../components/LoadingScreen/LoadingScreen';
import { useAccountNavigation } from '../../hooks/useAccountNavigation';
import { useSellerWhitelabelling } from '../../hooks/useSellerWhiteLabelling.hook';
import { GenericError } from '../../Pages/FailurePages/GenericError';
import { PayLinkExpired } from '../../Pages/FailurePages/PayLinkExpired';
import { PayLinkNotFound } from '../../Pages/FailurePages/PayLinkNotFound';
import { PayLinkTerminalState } from '../../Pages/FailurePages/PayLinkTerminalState';
import { FormalOffer } from '../../Seller/PayLinks/utils/Paylinks.types';
import { useThreatMetrix } from '../../utils/ThreatMetrix';
import { useThreatMetrixProfiling } from '../../utils/threatMetrix/ThreatMetrixProfiling';
import { trackSelectedCheckoutOption } from '../../utils/tracking';
import { CancelButton } from '../components/CancelButton/CancelButton';
import { LoginBanner } from '../components/LoginBanner';
import { PrivacyPolicy } from '../components/PrivacyPolicy/privacyPolicy';
import { WhiteLabelOverride } from '../components/WhiteLabelOverride/WhiteLabelOverride';
import { TEnabledOptions, useEnabledOptions } from '../hooks/useEnabledTerms';
import { useIsOverlay } from '../hooks/useIsOverlay';
import { useTrackLandingPageView } from '../hooks/useTrackLandingPageView';
import { LoginPanel } from '../LoginPanel/LoginPanel';
import { payNowCheckoutOnPayLink } from '../PayNow/checkout';
import { SIGNUP_BASE_PATH } from '../Signup/routes';
import {
  durationFromPricingOption,
  productFromDuration,
} from '../utils/product';
import { buildQueryString } from '../utils/queryParams';

export const PayLinkLanding = ({ payLinkId }: { payLinkId: string }) => {
  const { navigateToAccount } = useAccountNavigation();
  const decisionFlow = usePayLaterDecisionFlow();
  const cleanedPayLinkId = cleanPayLinkId(payLinkId);
  const { payLink, loadingPayLink, payLinkError } = useGetPayLinkByPayLinkId({
    payLinkId: cleanedPayLinkId,
  });

  const { enabledOptions, loadingOptions } = useEnabledOptions({
    pricing: payLink?.product_pricing,
    allowedOptions: payLink?.allowed_payment_terms,
  });
  const { seller, sellerError, loadingSeller } = useGetSellerByHandle(
    payLink?.seller_handle,
  );
  const { formalOffers, loadingFormalOffers } =
    useGetValidIwocapayFormalOffers();

  // White-label theme.
  const { loadingWhitelabelling } = useSellerWhitelabelling(
    payLink?.seller_handle,
  );

  useThreatMetrixProfiling(payLink?.seller_handle, payLink?.id);

  const [loginPanelOpen, setLoginPanelOpen] = useState(false);

  useTrackLandingPageView({
    payLinkType: 'custom',
    visibleOptions: enabledOptions,
    payLinkId: payLink?.id,
    productPricing: payLink?.product_pricing,
    sellerId: seller?.seller_id,
    loadingApis: loadingPayLink || loadingSeller,
  });

  useThreatMetrix();

  const isOverlay = useIsOverlay();

  if (sellerError) {
    return <GenericError errorText={"We can't find the seller"} />;
  }

  if (payLinkError) {
    return <PayLinkNotFound />;
  }

  if (
    loadingPayLink ||
    !payLink ||
    loadingSeller ||
    loadingOptions ||
    loadingFormalOffers ||
    loadingWhitelabelling
  ) {
    return <LoadingScreen />;
  }

  if (payLink.deleted_at) {
    return <GenericError errorText={SELLER_GET_ERROR} />;
  }

  if (payLink.in_terminal_state) {
    return <PayLinkTerminalState />;
  }

  if (payLink.source === 'iwocaPay_quickbooks') {
    return <PayLinkExpired />;
  }

  const pricingPromotions = getPricingPromotions({
    productPricing: payLink.product_pricing,
  });

  const buyerInterest = getBuyerInterest({
    productPricing: payLink.product_pricing,
    formalOffers: formalOffers,
  });

  const cancelUrl = payLink?.cancel_url;

  return (
    <div className="min-h-[100vh] w-full text-left text-m leading-[150%] text-denim-25">
      <div className="mx-auto flex max-w-[1440px] flex-col">
        <div className="m700:grid m700:flex-grow m700:gap-[2rem] m700:[grid-template-areas:'._payment-details_help-accordion'] m700:[grid-template-columns:1fr_2fr] m900:[grid-template-columns:1fr_2fr_1fr]">
          <div className="m700:mt-2xl m700:[grid-area:payment-details]">
            <CancelButton className="mb-xs" href={cancelUrl ?? undefined} />
            <WhiteLabelOverride componentType="background">
              <LoginBanner onClick={() => setLoginPanelOpen(true)} />
            </WhiteLabelOverride>
            <PaymentDetails
              isPreview={false}
              sellerName={payLink.seller_trading_name}
              pricingPromotions={pricingPromotions}
              buyerInterest={buyerInterest}
              onSubmit={onSubmit({
                payLink,
                formalOffers,
                enabledOptions,
                isOverlay,
                navigateToAccount,
                decisionFlow,
              })}
              initialValues={{
                amount: payLink.amount.toString(),
                reference: payLink.reference,
              }}
              lockAmount={true}
              lockReference={true}
              enabledOptions={enabledOptions}
              payLinkType="custom"
            />
            <PrivacyPolicy />
          </div>
          <div className="m700:flex m700:flex-col m700:justify-between m700:[grid-area:help-accordion]">
            <HelpTextAccordion
              className="mx-l mb-l mt-0 m700:mt-2xl"
              enabledOptions={enabledOptions}
            />
          </div>
        </div>
      </div>
      <LoginPanel
        open={loginPanelOpen}
        close={() => setLoginPanelOpen(false)}
      />
      <PayLaterDecisionModal status={decisionFlow.status} />
    </div>
  );
};

const onSubmit = ({
  payLink,
  formalOffers,
  enabledOptions,
  isOverlay,
  navigateToAccount,
  decisionFlow,
}: {
  payLink: PayLink;
  formalOffers?: FormalOffer[];
  enabledOptions: TEnabledOptions;
  isOverlay: boolean;
  navigateToAccount: (queryString?: string) => void;
  decisionFlow: ReturnType<typeof usePayLaterDecisionFlow>;
}) => {
  return ({ values, stateKey }: { values: FormValues; stateKey?: string }) => {
    const {
      paymentOption,
      payNowFullName,
      payNowEmailAddress,
      payNowMarketingConsent: marketingConsent,
    } = values;

    if (paymentOption === 'payNow')
      return payNowCheckoutOnPayLink({
        payLinkId: payLink.id,
        customerDetails: {
          fullName: payNowFullName,
          emailAddress: payNowEmailAddress,
        },
        marketingConsent,
        isOverlay,
      });

    return onSubmitPayLater({
      values,
      payLink,
      stateKey,
      formalOffers,
      enabledOptions,
      navigateToAccount,
      decisionFlow,
    });
  };
};

const onSubmitPayLater = async ({
  values,
  payLink,
  stateKey,
  formalOffers,
  enabledOptions,
  navigateToAccount,
  decisionFlow,
}: {
  values: FormValues;
  payLink: PayLink;
  stateKey?: string;
  formalOffers?: FormalOffer[];
  enabledOptions: TEnabledOptions;
  navigateToAccount: (queryString?: string) => void;
  decisionFlow: ReturnType<typeof usePayLaterDecisionFlow>;
}) => {
  const queryString = buildQueryString({ payLinkId: payLink.id });

  const selectedOption = values.paymentOption
    ? productFromDuration[durationFromPricingOption[values.paymentOption]!]
    : 'THREE_MONTHS';

  trackSelectedCheckoutOption({
    payLinkId: payLink.id,
    visibleOptions: enabledOptions,
    selectedOption: selectedOption,
  });

  if (stateKey) {
    await submitBuyerStepZeroReturningCustomer(
      stateKey,
      payLink,
      values.paymentOption!,
    );

    await fetchPostSharedUserAction({
      stateKey,
      body: { user_action: 'CHECKOUT_PAY_LINK' },
    });

    const payLinkAmount = parseFloat(values.amount);
    const queryParams = { payLinkId: payLink.id };
    redirectAfterPayLater({
      paymentOption: values.paymentOption,
      payLinkAmount,
      formalOffers,
      navigateToAccount,
      onAwaitDecision: decisionFlow.isEnabled
        ? () => void decisionFlow.trigger({ payLinkAmount, queryParams })
        : undefined,
      queryParams,
    });
    return;
  }

  const customerState = await submitBuyerStepZeroNewCustomer(
    values.emailAddress,
    values.marketingOptIn,
    payLink,
    values.paymentOption!,
  );

  await fetchPostSharedUserAction({
    stateKey: customerState.data!.state_key,
    body: { user_action: 'CHECKOUT_PAY_LINK' },
  });

  window.location.href = `${SIGNUP_BASE_PATH}${queryString}`;
};

const cleanPayLinkId = (payLinkId: string) => {
  return payLinkId.replace(' ', '');
};
